/**
 * Pure math for direct-manipulation sticker editing (move / resize / rotate).
 *
 * Anchors are per-mille (0–1000) in frame space; scale is per-mille where 1000
 * is the sticker's authored size. Every value we emit here stays inside the
 * ranges @desk/core's edit-graph validator accepts (anchor 0–1000, scale
 * 100–3000, rotation 0–360000), and inside the soft safe area the renderer
 * clamps to (6%–94%), so a committed move/resize/rotate can never trip
 * validation or push a sticker off-frame.
 */
import type { EditGraph } from "@desk/core";

/** Soft safe-area anchor bounds — mirrors `stickerInstanceFromOp`'s 6%–94% clamp. */
export const STICKER_ANCHOR_MIN_MILLI = 60;
export const STICKER_ANCHOR_MAX_MILLI = 940;

/**
 * Scale bounds for resize. Kept inside core's 100–3000 validation window and the
 * renderer's 0.35–2.2 draw clamp, so the handle never fights the layout.
 */
export const STICKER_SCALE_MIN_MILLI = 350;
export const STICKER_SCALE_MAX_MILLI = 2200;

export function clampStickerAnchorXMilli(xMilli: number): number {
  return clampRound(xMilli, STICKER_ANCHOR_MIN_MILLI, STICKER_ANCHOR_MAX_MILLI);
}

export function clampStickerAnchorYMilli(yMilli: number): number {
  return clampRound(yMilli, STICKER_ANCHOR_MIN_MILLI, STICKER_ANCHOR_MAX_MILLI);
}

export function clampStickerScaleMilli(scaleMilli: number): number {
  return clampRound(scaleMilli, STICKER_SCALE_MIN_MILLI, STICKER_SCALE_MAX_MILLI);
}

/** Normalize a rotation in millidegrees to [0, 360000) — core requires rotationMilli >= 0. */
export function clampStickerRotationMilli(rotationMilli: number): number {
  const m = Math.round(rotationMilli) % 360000;
  return m < 0 ? m + 360000 : m;
}

/** Screen pixel → clamped anchor milli, given the frame's bounding rect. */
export function screenToStickerAnchorMilli(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
): { anchorXMilli: number; anchorYMilli: number } | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  const xMilli = ((clientX - rect.left) / rect.width) * 1000;
  const yMilli = ((clientY - rect.top) / rect.height) * 1000;
  return {
    anchorXMilli: clampStickerAnchorXMilli(xMilli),
    anchorYMilli: clampStickerAnchorYMilli(yMilli),
  };
}

/** New clamped scale from a multiplicative factor applied to a base scale. */
export function scaleStickerByFactor(baseScaleMilli: number, factor: number): number {
  if (!Number.isFinite(factor) || factor <= 0) return clampStickerScaleMilli(baseScaleMilli);
  return clampStickerScaleMilli(baseScaleMilli * factor);
}

function clampRound(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, Math.round(n)));
}

/**
 * Sticker TIMING model: drop = scrub then tap (window starts at the playhead),
 * "appear for" replaces the old manual start/end fine-tune.
 */

/** "Appear for" presets in seconds (segmented control), default 0.5s. */
export const APPEAR_FOR_SECONDS = [0.1, 0.5, 1.0] as const;
export const DEFAULT_APPEAR_FOR_SECONDS = 0.5;

/** Convert an "appear for" duration in seconds to source ticks (min 1 tick). */
export function durationTicksFor(seconds: number, tickRate: number): number {
  return Math.max(1, Math.round(seconds * tickRate));
}

/**
 * Deterministic fresh sticker instance id (NO Math.random / Date.now — unlike
 * `nextOpId`, this value keys the resolved instance list, so it must be
 * reproducible from the graph alone).
 *
 * Uses the count of active sticker-kind ops in the append-only edit log
 * (`graph.ops` up to the undo/redo cursor). Provably collision-free: this
 * count strictly exceeds the count value used by every prior add still in the
 * active log, and it recomputes correctly across undo/redo (undone ops fall
 * outside the cursor, so a redo/re-add sequence never repeats an id still
 * live in the resolved list).
 */
export function nextStickerInstanceId(graph: EditGraph): string {
  const active = graph.ops.slice(0, graph.cursor);
  return "sticker-" + active.filter((o) => o.kind === "sticker").length;
}
